import * as XLSX from 'xlsx-js-style';

type ExcelCell = string | number | boolean | Date | null | undefined;

type GenerateExcelOptions = {
  fileName?: string;
  sheetName?: string;
  postProcessWorksheet?: (worksheet: XLSX.WorkSheet) => void;
};

// Excel limits sheet names to 31 chars and forbids ":\/?*[]". We also dedupe
// duplicates within a single workbook by appending " (n)" suffixes.
const MAX_SHEET_NAME_LEN = 31;
const FORBIDDEN_SHEET_NAME_CHARS = /[:\\/?*[\]]/g;

const sanitizeSheetName = (raw: string): string => {
  const stripped = (raw || 'Sheet').replace(FORBIDDEN_SHEET_NAME_CHARS, '');
  const trimmed = stripped.trim() || 'Sheet';
  return trimmed.length > MAX_SHEET_NAME_LEN
    ? trimmed.slice(0, MAX_SHEET_NAME_LEN)
    : trimmed;
};

const uniqueSheetName = (raw: string, used: Set<string>): string => {
  const base = sanitizeSheetName(raw);
  if (!used.has(base)) {
    used.add(base);
    return base;
  }
  let i = 2;
  while (true) {
    const suffix = ` (${i})`;
    const candidate =
      base.length + suffix.length <= MAX_SHEET_NAME_LEN
        ? `${base}${suffix}`
        : `${base.slice(0, MAX_SHEET_NAME_LEN - suffix.length)}${suffix}`;
    if (!used.has(candidate)) {
      used.add(candidate);
      return candidate;
    }
    i += 1;
  }
};

// Build a styled, RTL, zebra-striped worksheet from a 2D array. Extracted so
// both the single-sheet and the multi-sheet entry points share the exact same
// formatting/layout logic.
const buildStyledWorksheet = (data: ExcelCell[][]): XLSX.WorkSheet => {
  const worksheet = XLSX.utils.aoa_to_sheet(data);

  // Display worksheet in RTL (Hebrew-friendly)
  (worksheet as any)['!dir'] = 'rtl';

  // Auto-fit column widths based on content length.
  // Excel doesn't truly "auto-fit" on open; this approximates by setting wch.
  const maxCols = Math.max(0, ...data.map((row) => row.length));
  const colWidths = Array.from({ length: maxCols }, (_, colIdx) => {
    const maxLen = Math.max(
      0,
      ...data.map((row) => {
        const v = row[colIdx] ?? '';
        return String(v).length;
      }),
    );

    const padded = maxLen + 2;
    return { wch: Math.min(60, Math.max(10, padded)) };
  });
  (worksheet as any)['!cols'] = colWidths;

  // Professional styling: header, borders, zebra rows.
  const ref = (worksheet as any)['!ref'] as string | undefined;
  if (ref) {
    const range = XLSX.utils.decode_range(ref);

    const border = {
      top: { style: 'thin', color: { rgb: 'D0D5DD' } },
      bottom: { style: 'thin', color: { rgb: 'D0D5DD' } },
      left: { style: 'thin', color: { rgb: 'D0D5DD' } },
      right: { style: 'thin', color: { rgb: 'D0D5DD' } },
    } as const;

    for (let r = range.s.r; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const addr = XLSX.utils.encode_cell({ r, c });
        const cell = (worksheet as any)[addr];
        if (!cell) {
          continue;
        }

        const isHeader = r === range.s.r;
        // Requested pattern:
        // - row 1 (header): bg1
        // - row 2: bg2
        // - row 3: bg3
        // - row 4: bg2
        // - row 5: bg3 ...
        const dataRowIndex = r - range.s.r - 1; // 0-based (first data row)
        const isBg2 = dataRowIndex % 2 === 0;

        const fillColor = isHeader
          ? '1F4E79' // bg1: dark blue header
          : isBg2
            ? 'F3F4F6' // bg2: light gray
            : 'FFFFFF'; // bg3: white

        cell.s = {
          ...(cell.s ?? {}),
          alignment: {
            ...((cell.s ?? {}).alignment ?? {}),
            horizontal: 'right',
            vertical: 'center',
            wrapText: true,
          },
          border,
          fill: {
            patternType: 'solid',
            fgColor: { rgb: fillColor },
          },
          font: isHeader
            ? { bold: true, color: { rgb: 'FFFFFF' } }
            : { color: { rgb: '111827' } },
        };
      }
    }

    // Slightly taller header row
    (worksheet as any)['!rows'] = [{ hpt: 20 }];
  }

  return worksheet;
};

const downloadWorkbook = (
  workbook: XLSX.WorkBook,
  fileName: string | undefined,
): void => {
  const binaryString = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'binary',
  });

  const buffer = new ArrayBuffer(binaryString.length);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < binaryString.length; i++) {
    view[i] = binaryString.charCodeAt(i) & 0xff;
  }

  const blob = new Blob([buffer], { type: 'application/octet-stream' });

  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${fileName ?? 'data'}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const generateAndDownloadExcelFile = (
  data: ExcelCell[][],
  options: GenerateExcelOptions = {},
): void => {
  const workbook = XLSX.utils.book_new();
  (workbook as any).Workbook = { Views: [{ RTL: true }] };

  const worksheet = buildStyledWorksheet(data);

  if (options.postProcessWorksheet) {
    options.postProcessWorksheet(worksheet);
  }

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    sanitizeSheetName(options.sheetName ?? 'Sheet1'),
  );

  downloadWorkbook(workbook, options.fileName);
};

export type ExcelSheetInput = {
  // 2D array of cells; first row is treated as the styled header.
  data: ExcelCell[][];
  // Human-readable sheet name. Auto-sanitized and de-duplicated across the
  // workbook (Excel forbids ":\/?*[]" and caps names at 31 chars).
  sheetName: string;
  // Optional per-sheet hook (e.g. set merges, freeze panes).
  postProcessWorksheet?: (worksheet: XLSX.WorkSheet) => void;
};

type GenerateMultiSheetExcelOptions = {
  fileName?: string;
};

/**
 * Build a single .xlsx workbook from multiple sheets and trigger a download.
 *
 * Each sheet is styled identically to {@link generateAndDownloadExcelFile}
 * (RTL, dark blue bold header, zebra rows, thin borders, auto-fit columns).
 * Sheet names are sanitized and de-duplicated automatically.
 *
 * Use this when you need to export several related datasets in one file
 * (e.g. "all selected deposits → one sheet per deposit"). For single-sheet
 * exports prefer {@link generateAndDownloadExcelFile}.
 */
export const generateAndDownloadMultiSheetExcelFile = (
  sheets: ExcelSheetInput[],
  options: GenerateMultiSheetExcelOptions = {},
): void => {
  const workbook = XLSX.utils.book_new();
  (workbook as any).Workbook = { Views: [{ RTL: true }] };

  const usedNames = new Set<string>();
  sheets.forEach((sheet, idx) => {
    const worksheet = buildStyledWorksheet(sheet.data);
    if (sheet.postProcessWorksheet) {
      sheet.postProcessWorksheet(worksheet);
    }
    const name = uniqueSheetName(
      sheet.sheetName || `Sheet${idx + 1}`,
      usedNames,
    );
    XLSX.utils.book_append_sheet(workbook, worksheet, name);
  });

  downloadWorkbook(workbook, options.fileName);
};

export default generateAndDownloadExcelFile;
